import { AddDiv, CreateDomElement } from '../engine/viewer/domutils.js';
import { ButtonDialog } from './dialog.js';
import { FLoc, Loc } from '../engine/core/localization.js';
import { AddGraceWarning, FormatDate, ShowMessageDialog } from './storagedialog.js';
import { AddCheckbox } from './utils.js';

function AddPlanLine (parentDiv, state)
{
    let planDiv = AddDiv (parentDiv, 'ov_billing_plan');
    if (state.subscriptionEndsAt) {
        planDiv.innerText = FLoc ('{0}, paid until {1}', state.planTitle, FormatDate (state.subscriptionEndsAt));
    } else {
        planDiv.innerText = state.planTitle;
    }
    return planDiv;
}

function OrderStatusText (order)
{
    if (order.status === 'requested') {
        // An invoice nobody asked for needs saying so, or it reads as a
        // request the person forgot making.
        if (order.auto) {
            return FLoc ('Renewal on {0}: we are issuing an invoice for the next period.',
                FormatDate (order.createdAt));
        }
        return FLoc ('Request accepted on {0}. We will issue an invoice and write to the account address.',
            FormatDate (order.createdAt));
    }
    if (order.status === 'invoiced') {
        return FLoc ('Invoice {0} has been issued. The subscription starts once the money arrives.',
            order.reference || '');
    }
    return '';
}

// Renewal here is paperwork, not payment, and the text says exactly that: the
// invoice appears by itself, the money still moves by hand.
function AddAutoRenewLine (parentDiv, billing, state)
{
    if (!state.subscriptionEndsAt) {
        return;
    }
    let lineDiv = AddDiv (parentDiv, 'ov_billing_autorenew');
    let messageDiv = AddDiv (parentDiv, 'ov_auth_message');
    messageDiv.style.display = 'none';

    let check = AddCheckbox (lineDiv, 'billing_auto_renew', Loc ('Renew automatically'),
        state.autoRenew === true, () => {
            let wanted = check.checked;
            billing.SetAutoRenew (wanted).catch ((error) => {
                check.checked = !wanted;
                messageDiv.innerText = error.message;
                messageDiv.classList.add ('error');
                messageDiv.style.display = 'block';
            });
        });

    AddDiv (parentDiv, 'ov_billing_note', FLoc (
        'We issue the invoice for the next period {0} days before the subscription ends. Nothing is charged by itself — cards are not connected, the invoice is paid the usual way.',
        state.renewLeadDays || 7));
}

export function ShowBillingDialog (billing, callbacks)
{
    let dialog = new ButtonDialog ();
    let contentDiv = dialog.Init (Loc ('Subscription'), [
        {
            name : Loc ('Close'),
            onClick () {
                dialog.Close ();
            }
        }
    ]);
    dialog.GetContentDiv ().classList.add ('ov_auth_dialog', 'ov_billing_dialog');

    let bodyDiv = AddDiv (contentDiv, 'ov_billing_body');
    bodyDiv.innerText = Loc ('Loading…');

    billing.GetState ().then ((state) => {
        bodyDiv.innerHTML = '';
        AddGraceWarning (bodyDiv, state);
        AddPlanLine (bodyDiv, state);
        AddAutoRenewLine (bodyDiv, billing, state);

        if (state.openOrder !== null) {
            AddDiv (bodyDiv, 'ov_storage_message', OrderStatusText (state.openOrder));
            AddDiv (bodyDiv, 'ov_billing_note', FLoc ('{0} for {1}', state.openOrder.amountText,
                state.openOrder.period === 'year' ? Loc ('a year') : Loc ('a month')));
            return;
        }
        if (state.prices === null) {
            return;
        }

        AddDiv (bodyDiv, 'ov_auth_label', Loc ('What the subscription gives'));
        AddDiv (bodyDiv, 'ov_storage_message', Loc ('10 GB of storage instead of 1 GB, scenes up to 5 GB, and models are kept without a time limit.'));

        // Period is a plain pair of radio buttons rather than a fancy switch:
        // there are two of them and they have to be readable, not clever.
        let periodDiv = AddDiv (bodyDiv, 'ov_billing_periods');
        let chosenPeriod = 'month';
        let periodButtons = [];
        for (let period of ['month', 'year']) {
            let price = state.prices[period];
            if (price === undefined) {
                continue;
            }
            let buttonDiv = AddDiv (periodDiv, 'ov_billing_period');
            AddDiv (buttonDiv, 'ov_billing_period_price', price.text);
            AddDiv (buttonDiv, 'ov_billing_period_title', FLoc ('per {0}', price.title));
            buttonDiv.addEventListener ('click', () => {
                chosenPeriod = period;
                for (let other of periodButtons) {
                    other.div.classList.toggle ('selected', other.period === period);
                }
            });
            periodButtons.push ({ period : period, div : buttonDiv });
        }
        if (periodButtons.length > 0) {
            periodButtons[0].div.classList.add ('selected');
        }

        AddDiv (bodyDiv, 'ov_auth_label', Loc ('Company details for the invoice'));
        let companyInput = CreateDomElement ('textarea', 'ov_billing_company');
        companyInput.setAttribute ('rows', '3');
        companyInput.setAttribute ('placeholder', Loc ('Name, ИНН, КПП — or leave empty if the invoice is for a person'));
        bodyDiv.appendChild (companyInput);

        let messageDiv = AddDiv (bodyDiv, 'ov_auth_message');
        messageDiv.style.display = 'none';

        // Said out loud rather than hidden behind a disabled button: paying by
        // card does not work yet, and pretending otherwise wastes somebody's
        // time at exactly the wrong moment.
        AddDiv (bodyDiv, 'ov_auth_note', Loc ('Card payment is not connected yet. We issue an invoice and write to the account address; access is switched on once the money arrives.'));

        let requestDiv = AddDiv (bodyDiv, 'ov_button ov_billing_request', Loc ('Request an invoice'));
        requestDiv.addEventListener ('click', async () => {
            if (requestDiv.classList.contains ('busy')) {
                return;
            }
            requestDiv.classList.add ('busy');
            messageDiv.style.display = 'none';
            try {
                await billing.RequestInvoice ('pro', chosenPeriod, companyInput.value);
                dialog.Close ();
                ShowMessageDialog (
                    Loc ('Subscription'),
                    Loc ('Request accepted. We will issue an invoice and write to the account address.')
                );
                if (callbacks && callbacks.onRequested) {
                    callbacks.onRequested ();
                }
            } catch (error) {
                messageDiv.innerText = error.message;
                messageDiv.classList.add ('error');
                messageDiv.style.display = 'block';
            } finally {
                requestDiv.classList.remove ('busy');
            }
        });
    }).catch ((error) => {
        bodyDiv.innerHTML = '';
        AddDiv (bodyDiv, 'ov_auth_message error', error.message);
    });

    dialog.Open ();
    return dialog;
}
